import type { SelectorSet } from '@annotate/shared';

export const HOST_ATTR = 'data-annotate-root';

/** Elements belonging to the widget itself. Never anchor to, index, or hit-test
 *  our own UI — a comment about a comment marker is nonsense. */
export function isOurs(node: Node | null): boolean {
  let el: Node | null = node;
  while (el) {
    if (el instanceof Element && el.hasAttribute(HOST_ATTR)) return true;
    el = el.parentNode ?? (el as { host?: Node }).host ?? null;
  }
  return false;
}

/**
 * Classes worth putting in a selector.
 *
 * Framework-generated names (`css-1x8ky3f`, `sc-bdVaJa`, hashed CSS-module
 * suffixes) change on every build, so a selector built from them looks stable
 * and silently stops matching the next time the page is generated. Cheap to
 * exclude, expensive to debug.
 */
function usefulClass(cls: string): boolean {
  if (!cls || cls.length > 40) return false;
  if (/^(css|sc|jsx|emotion|MuiBox|chakra)-/i.test(cls)) return false;
  if (/[a-f0-9]{6,}/i.test(cls) && /\d/.test(cls)) return false;
  if (/^\d/.test(cls)) return false;
  return true;
}

function stableId(el: Element): string | null {
  const id = el.getAttribute('id');
  if (!id) return null;
  // React/Radix-style generated ids (`:r3:`, `radix-:r7:`) and long hex blobs are
  // regenerated per render, so they are no more durable than a random number.
  if (/[:.]/.test(id)) return null;
  if (/^\d/.test(id)) return null;
  if (/[a-f0-9]{8,}/i.test(id)) return null;
  return id;
}

function cssEscape(value: string): string {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : value.replace(/[^\w-]/g, '\\$&');
}

/** The token that makes an element findable on its own, if it has one. */
function anchorToken(el: Element): string | null {
  const annotateId = el.getAttribute('data-annotate-id');
  if (annotateId) return `[data-annotate-id="${cssEscape(annotateId)}"]`;
  const id = stableId(el);
  if (id) return `#${cssEscape(id)}`;
  const testId = el.getAttribute('data-testid');
  if (testId) return `[data-testid="${cssEscape(testId)}"]`;
  return null;
}

function nthOfType(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const parent = el.parentElement;
  if (!parent) return tag;
  const siblings = Array.from(parent.children).filter((c) => c.tagName === el.tagName);
  if (siblings.length === 1) return tag;
  return `${tag}:nth-of-type(${siblings.indexOf(el) + 1})`;
}

function pathFrom(el: Element, stopAt: Element | null): string {
  const parts: string[] = [];
  let cur: Element | null = el;
  while (cur && cur !== stopAt && cur !== document.documentElement) {
    parts.unshift(nthOfType(cur));
    cur = cur.parentElement;
  }
  const prefix = stopAt ? `${anchorToken(stopAt)} > ` : 'body > ';
  return prefix + parts.join(' > ');
}

function matchesUniquely(selector: string, el: Element): boolean {
  try {
    const found = document.querySelectorAll(selector);
    return found.length === 1 && found[0] === el;
  } catch {
    return false;
  }
}

/**
 * Build several independent ways to find `el`, ordered most to least durable,
 * and keep only the ones that actually resolve to it right now.
 *
 * Generating candidates without verifying them is how anchoring quietly rots: a
 * selector that was never correct is indistinguishable at read time from one
 * whose target has since moved.
 */
export function selectorsFor(el: Element): SelectorSet {
  const candidates: string[] = [];

  const own = anchorToken(el);
  if (own) candidates.push(own);

  // Path anchored at the nearest ancestor that has a durable token of its own.
  let ancestor: Element | null = el.parentElement;
  while (ancestor) {
    if (anchorToken(ancestor)) {
      candidates.push(pathFrom(el, ancestor));
      break;
    }
    ancestor = ancestor.parentElement;
  }

  const classes = Array.from(el.classList).filter(usefulClass).slice(0, 3);
  if (classes.length > 0) {
    candidates.push(`${el.tagName.toLowerCase()}.${classes.map(cssEscape).join('.')}`);
  }

  // Absolute path. Brittle against any structural edit, which is exactly why it
  // is last — but it always exists, so there is always something to try.
  candidates.push(pathFrom(el, null));

  const verified = [...new Set(candidates)].filter((s) => matchesUniquely(s, el));
  if (verified.length === 0) {
    // Nothing unique resolves — usually a repeated element with no distinguishing
    // features. Record the absolute path anyway and let resolution report an
    // orphan if it stops matching.
    const fallback = pathFrom(el, null);
    return { primary: fallback, fallbacks: [] };
  }

  return { primary: verified[0]!, fallbacks: verified.slice(1) };
}

/** First selector in the set that resolves to a real, laid-out element. */
export function resolveSelectors(set: SelectorSet): Element | null {
  for (const selector of [set.primary, ...set.fallbacks]) {
    let found: Element | null = null;
    try {
      found = document.querySelector(selector);
    } catch {
      continue;
    }
    if (found && !isOurs(found)) return found;
  }
  return null;
}

/** An element with no box cannot hold a marker. Treating `display:none` as a
 *  successful resolve would park every affected pin at the page origin. */
export function hasBox(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 || r.height > 0;
}
